import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaBookOpen, FaImage } from 'react-icons/fa';
import { helperFunction } from '../helpers/api_call_helper';
import { getBlogs } from '../helpers/backend_helper';
import { normalizeBlog } from '../helpers/utils';

const getBlogsPayload = (response) => {
  const payload = response?.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.blogs)) return payload.blogs;
  if (Array.isArray(payload?.data?.blogs)) return payload.data.blogs;
  if (Array.isArray(payload)) return payload;
  return [];
};

const stripHtml = (html = '') => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
};

function BlogMediaPreview({ blog }) {
  const cover = blog.cover;

  if (!cover) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-[rgba(201,169,98,0.08)] text-[var(--gold)]">
        <FaBookOpen className="text-5xl opacity-70" />
      </div>
    );
  }

  if (cover.type === 'video') {
    return <video src={cover.url} className="h-full w-full object-cover" muted playsInline controlsList="nodownload" />;
  }

  return <img src={cover.url} alt={blog.imageAlt || blog.title} className="h-full w-full object-cover" loading="lazy" decoding="async" />;
}

export default function BlogsSection() {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  useEffect(() => {
    let mounted = true;

    helperFunction(
      async () => {
        const response = await getBlogs({ limit: 3 });
        const nextBlogs = getBlogsPayload(response).map(normalizeBlog).slice(0, 3);
        if (mounted) setBlogs(nextBlogs);
        return response;
      },
      setLoading,
      undefined,
      (message) => {
        if (mounted) setFetchError(message);
      },
    );

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="events-section home-preview-section" id="blogs">
      <div className="section">
        <div className="section-header reveal in-view section-header-with-nav">
          <div className="header-content">
            <div className="section-label">Author Journal</div>
            <h2 className="section-title">Latest Blogs</h2>
            <p className="section-subtitle">
              Read reflections, launch stories, and literary notes from Kitabon Ki Dolat.
            </p>
          </div>
          <button className="preview-more-btn" type="button" onClick={() => navigate('/blogs')}>
            More Blogs <FaArrowRight />
          </button>
        </div>

        <div className="content-preview-grid">
          {loading && !blogs.length && Array.from({ length: 3 }).map((_, i) => (
            <div key={`skel-${i}`} className="event-card animate-pulse flex flex-col h-[400px]">
              <div className="event-image h-48 w-full bg-[var(--shimmer-base)] rounded-t-[1.15rem]"></div>
              <div className="event-info p-6 gap-3 flex flex-col">
                <div className="h-3 w-1/4 rounded bg-[var(--shimmer-gold)]"></div>
                <div className="h-5 w-5/6 rounded bg-[var(--shimmer-light)] mt-2"></div>
                <div className="h-3 w-full rounded bg-[var(--shimmer-base)] mt-4"></div>
                <div className="h-3 w-full rounded bg-[var(--shimmer-base)]"></div>
                <div className="h-3 w-2/3 rounded bg-[var(--shimmer-base)]"></div>
              </div>
            </div>
          ))}

          {!loading && !blogs.length && (
            <div className="event-card reveal in-view p-8 text-[var(--text-dim)]">
              {fetchError || 'No blogs available yet.'}
            </div>
          )}

          {blogs.map((blog, index) => {
            const preview = stripHtml(blog.content);
            const detailPath = `/blog/${blog.slug || blog.urlSlug || blog.id}`;

            return (
              <div
                key={blog.id}
                className="event-card reveal in-view cursor-pointer"
                style={{ transitionDelay: `${index * 0.1}s` }}
                onClick={() => navigate(detailPath)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => event.key === 'Enter' && navigate(detailPath)}
                aria-label={`Read ${blog.title}`}
              >
                <div className="event-image">
                  <BlogMediaPreview blog={blog} />
                  <div className="event-image-overlay" />
                  {blog.media.length > 0 && (
                    <span className="event-date-badge inline-flex items-center gap-2">
                      <FaImage /> {blog.media.length}
                    </span>
                  )}
                </div>

                <div className="event-info">
                  <div className="mb-2 text-xs font-bold uppercase tracking-[0.18em] text-[var(--gold)]">
                    {blog.date || 'Blog'}
                  </div>
                  <h3 className="event-title">{blog.title}</h3>
                  <p className="text-sm leading-7 text-[var(--text-dim)]">
                    {preview.slice(0, 150)}{preview.length > 150 ? '...' : ''}
                  </p>
                  <button
                    className="view-event-details inline-flex items-center gap-2"
                    onClick={(event) => {
                      event.stopPropagation();
                      navigate(detailPath);
                    }}
                  >
                    Read Blog <FaArrowRight />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
